import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Trophy, Shield, TrendingUp, DollarSign, Users, 
  ArrowRight, Check, Star, Zap, ChevronLeft,
  Target, BarChart3, Award, Clock, Lock, Sparkles,
  CheckCircle2, AlertTriangle, Coins
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const RealFundedAccounts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [eligibilityStatus, setEligibilityStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const checkEligibility = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await api.get('/api/elite-funded/eligibility');
        setEligibilityStatus(response.data);
      } catch (error) {
        console.error('Failed to check eligibility:', error);
      } finally {
        setLoading(false);
      }
    };
    
    checkEligibility();
  }, [user]);

  const handleApply = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    setApplying(true);
    try {
      await api.post('/api/elite-funded/apply');
      toast.success('Application submitted! Our team will review your trading history.');
      setEligibilityStatus({ ...eligibilityStatus, application_status: 'pending' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setApplying(false);
    }
  };

  const accountSizes = [
    { size: '$50,000', price: '$4,999', maxDrawdown: '20%', dailyDrawdown: '10%', profitSplit: '80/20' },
    { size: '$100,000', price: '$9,499', maxDrawdown: '20%', dailyDrawdown: '10%', profitSplit: '80/20' },
    { size: '$200,000', price: '$17,999', maxDrawdown: '20%', dailyDrawdown: '10%', profitSplit: '80/20' },
  ];

  const benefits = [
    {
      icon: DollarSign,
      title: 'Real Capital, Real Profits',
      description: 'Trade with actual funded capital in live market conditions. Your profits are 100% real.'
    },
    {
      icon: Shield,
      title: '100% Guaranteed Payouts',
      description: 'Every payout request is processed and guaranteed. No hidden terms or conditions.'
    },
    {
      icon: TrendingUp,
      title: '80% Profit Split',
      description: 'Keep 80% of all profits you generate. We only take 20% as our management fee.'
    },
    {
      icon: Trophy,
      title: 'Selection-Based Access',
      description: 'Only proven traders with demonstrated success on Funded Instant accounts qualify.'
    },
  ];

  const requirements = [
    'Successfully traded on a Funded Instant account ($50K, $100K, or $200K)',
    'Minimum 20 trading days of activity',
    'Consistent profitability over 3+ months',
    'No major rule violations on record',
    'Completed KYC verification',
  ];

  const rules = [
    { rule: 'Maximum Drawdown', value: '20%', description: 'Maximum loss from peak equity - flexible for traders' },
    { rule: 'Daily Drawdown', value: '10%', description: 'Maximum daily loss limit' },
    { rule: 'Profit Split', value: '80/20', description: '80% to you, 20% to us' },
    { rule: 'Payouts', value: '100% Guaranteed', description: 'All profits are paid out' },
  ];

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-24 pb-16">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="text-gray-400 hover:text-white"
          data-testid="back-to-home"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>
      </div>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        </div>

        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Elite Badge */}
            <Badge className="mb-6 px-5 py-2 bg-green-500/10 backdrop-blur-sm border border-green-500/30 text-green-300 text-sm font-medium">
              <Trophy className="w-4 h-4 mr-2" />
              ELITE TRADER PROGRAM
            </Badge>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black text-white mb-4" data-testid="elite-funded-title">
              ELITE FUNDED
              <br />
              <span className="bg-gradient-to-r from-green-400 to-emerald-500 bg-clip-text text-transparent">
                TRADER
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Trade with <span className="text-green-400 font-bold">real capital</span> under live market conditions.
              Selected traders get the opportunity to manage actual funded accounts with 
              <span className="text-green-400 font-bold"> 100% guaranteed payouts</span>.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {user ? (
                <Button
                  size="lg"
                  onClick={handleApply}
                  disabled={applying || eligibilityStatus?.application_status === 'pending'}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-heading uppercase tracking-wider rounded-full px-10 py-7 shadow-lg shadow-green-500/25"
                  data-testid="apply-btn"
                >
                  {applying ? 'Submitting...' : 
                   eligibilityStatus?.application_status === 'pending' ? 'Application Pending' :
                   eligibilityStatus?.is_eligible ? 'Apply Now' : 'Check Eligibility'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              ) : (
                <Button
                  size="lg"
                  onClick={() => navigate('/login')}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-heading uppercase tracking-wider rounded-full px-10 py-7 shadow-lg shadow-green-500/25"
                >
                  Login to Apply
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/challenges')}
                className="border-white/20 text-white hover:bg-white/5 rounded-full px-10 py-7"
              >
                Start with Funded Instant
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="flex justify-center gap-8 mt-12">
              <div className="text-center">
                <p className="text-3xl font-bold text-white">$200K</p>
                <p className="text-gray-500 text-sm">Max Account Size</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-green-400">80%</p>
                <p className="text-gray-500 text-sm">Profit Share</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white">100%</p>
                <p className="text-gray-500 text-sm">Guaranteed Payouts</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Selection Notice */}
      <section className="py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-green-500/10 rounded-3xl p-8 md:p-12 border border-green-500/20"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Lock className="w-8 h-8 text-green-400" />
              <h2 className="font-heading text-3xl font-bold text-white">SELECTION-BASED ACCESS</h2>
            </div>
            <p className="text-gray-300 text-center max-w-3xl mx-auto text-lg leading-relaxed">
              Elite Funded Trader accounts are <span className="text-green-400 font-bold">NOT</span> available for open purchase.
              Traders must first demonstrate their skills by successfully trading on a 
              <span className="text-green-400 font-bold"> Funded Instant Account</span>. 
              Once you prove consistent profitability, you'll be invited to apply for an Elite Funded Trader account.
            </p>
          </motion.div>
        </div>
      </section>

      {/* KBTCOIN Rewards for Elite */}
      <section className="py-10 sm:py-16">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-yellow-500/10 via-orange-500/10 to-yellow-500/10 rounded-3xl p-8 md:p-12 border border-yellow-500/20"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Coins className="w-8 h-8 text-yellow-400" />
              <h2 className="font-heading text-3xl font-bold text-white">KBTCOIN REWARDS</h2>
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Coming Soon</Badge>
            </div>
            <p className="text-gray-300 text-center max-w-3xl mx-auto text-lg leading-relaxed mb-6">
              Elite Funded Traders will receive <span className="text-yellow-400 font-bold">KBTCOIN token rewards</span> based on their performance. 
              Token allocations for Elite accounts will be announced when the program launches.
            </p>
            <div className="grid sm:grid-cols-3 gap-4 max-w-2xl mx-auto">
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <p className="text-yellow-400 font-bold text-xl">Monthly</p>
                <p className="text-gray-400 text-sm">Performance Bonuses</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <p className="text-yellow-400 font-bold text-xl">5-30% APY</p>
                <p className="text-gray-400 text-sm">Staking Rewards</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <p className="text-yellow-400 font-bold text-xl">Priority</p>
                <p className="text-gray-400 text-sm">Seed Round Access</p>
              </div>
            </div>
            <div className="flex justify-center mt-6">
              <Button
                onClick={() => navigate('/crypto')}
                className="bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white font-bold rounded-full px-8"
              >
                <Coins className="w-4 h-4 mr-2" />
                Learn About KBTCOIN
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 sm:py-20 bg-[hsl(220,20%,6%)]">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 px-4 py-1 bg-green-500/10 text-green-400 border border-green-500/20">
              <Star className="w-4 h-4 mr-2" />
              BENEFITS
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              WHY <span className="text-green-400">ELITE FUNDED TRADER</span>?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="bg-[hsl(220,20%,7%)] border border-white/10 hover:border-green-500/30 transition-all h-full">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 mb-4 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                      <benefit.icon className="w-7 h-7 text-green-400" />
                    </div>
                    <h3 className="font-heading text-lg font-bold text-white mb-2">{benefit.title}</h3>
                    <p className="text-gray-500 text-sm">{benefit.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Account Sizes */}
      <section className="py-12 sm:py-20" data-testid="account-sizes">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <Badge className="mb-4 px-4 py-1 bg-blue-500/10 text-blue-400 border border-blue-500/20">
              <DollarSign className="w-4 h-4 mr-2" />
              ACCOUNT OPTIONS
            </Badge>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              CHOOSE YOUR <span className="text-green-400">CAPITAL</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Once approved, select your preferred account size based on your trading style.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {accountSizes.map((account, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className={`h-full border ${index === 2 ? 'bg-gradient-to-b from-green-500/10 to-[hsl(220,20%,7%)] border-green-500/30' : 'bg-[hsl(220,20%,7%)] border-white/10'} hover:border-green-500/50 transition-all`}>
                  {index === 2 && (
                    <div className="bg-green-500 text-white text-xs font-bold px-3 py-1 text-center rounded-t-lg">
                      MOST POPULAR
                    </div>
                  )}
                  <CardContent className="p-8 text-center">
                    <h3 className="font-heading text-4xl font-black text-white mb-2">{account.size}</h3>
                    <p className="text-green-400 font-bold text-2xl mb-6">{account.price}</p>
                    
                    <div className="space-y-3 text-left">
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <span className="text-gray-400">Max Drawdown</span>
                        <span className="text-white font-medium">{account.maxDrawdown}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-white/5">
                        <span className="text-gray-400">Daily Drawdown</span>
                        <span className="text-white font-medium">{account.dailyDrawdown}</span>
                      </div>
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-400">Profit Split</span>
                        <span className="text-green-400 font-bold">{account.profitSplit}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility Requirements */}
      <section className="py-12 sm:py-20 bg-[hsl(220,20%,6%)]" data-testid="requirements">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Badge className="mb-4 px-4 py-1 bg-purple-500/10 text-purple-400 border border-purple-500/20">
                <Target className="w-4 h-4 mr-2" />
                ELIGIBILITY
              </Badge>
              <h2 className="font-heading text-4xl font-black text-white mb-6">
                HOW TO <span className="text-green-400">QUALIFY</span>
              </h2>
              <p className="text-gray-400 mb-8">
                Elite Funded Trader accounts are reserved for traders who have demonstrated consistent 
                profitability and risk management skills. Here's what you need:
              </p>
              
              <div className="space-y-4">
                {requirements.map((req, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-green-400 mt-0.5 shrink-0" />
                    <span className="text-gray-300">{req}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="bg-[hsl(220,20%,7%)] border border-white/10">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-400" />
                    Trading Rules
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {rules.map((item, index) => (
                    <div key={index} className="flex justify-between items-center py-3 border-b border-white/5 last:border-0">
                      <div>
                        <p className="text-white font-medium">{item.rule}</p>
                        <p className="text-gray-500 text-sm">{item.description}</p>
                      </div>
                      <span className={`font-bold ${item.rule === 'Profit Split' || item.rule === 'Payouts' ? 'text-green-400' : 'text-yellow-400'}`}>
                        {item.value}
                      </span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20">
        <div className="max-w-4xl mx-auto px-3 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-green-500/30">
              <Award className="w-10 h-10 text-white" />
            </div>
            <h2 className="font-heading text-4xl lg:text-5xl font-black text-white mb-4">
              READY TO <span className="text-green-400">TRADE REAL CAPITAL</span>?
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              Start your journey with a Funded Instant account. Prove your skills, 
              and you could be managing real capital with 100% guaranteed payouts.
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/challenges')}
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-heading uppercase tracking-wider rounded-full px-12 py-7 shadow-2xl shadow-green-500/30 transition-all hover:scale-105"
              data-testid="start-challenge-btn"
            >
              START YOUR FUNDED INSTANT
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default RealFundedAccounts;
